<i18n src="@/translations/FinPortfolioOverviewPart.json"></i18n>
<template>
  <div style="background-color: #f5f8f9" :class="assetClassId ? assetClassId : 'overview'">
    <div v-if="hasAssetClassTransactionHistory">
      <v-row>
        <v-col cols="12" sm="6" md="4" xl="3">
          <fin-portfolio-value-change :addSign="false"
                                      :color="$finColors.primaryDark"
                                      :from="portfolioInitialValue()"
                                      :to="portfolioCurrentValue()"
                                      :value-date="portfolioCurrentValueDate()"
                                      class="fin-portfolio-value-change"
                                      hint="Wertentwicklung seit Kauf"
                                      title="Aktueller Wert">
          </fin-portfolio-value-change>
        </v-col>
        <v-col cols="12" sm="6" md="4" xl="3">
          <fin-portfolio-value :valueLine="portfolioYield()"
                               class="fin-portfolio-rendite"
                               :title="$t('FinPortfolioOverviewPart.yield_since_start')">
            <template #valueSubline>
              <v-row class="mt-3">
                <v-col v-if="portfolioYield1D()">
                  <div class="portfolio-value-box__hint">Heute</div>
                  <div :style="{color: portfolio1DColor}">
                    {{ portfolioYield1D() | percent }}
                  </div>
                </v-col>
                <v-col v-if="portfolioYield1Y()">
                  <div class="portfolio-value-box__hint">Seit einem Jahr</div>
                  <div :style="{color: portfolio1YColor}">
                    {{ portfolioYield1Y() | percent }}
                  </div>
                </v-col>
              </v-row>
            </template>
          </fin-portfolio-value>
        </v-col>
        <v-col cols="12" md="4" xl="3">
          <fin-portfolio-value-change v-if="!assetClass"
                                      :addSign="false"
                                      class="fin-portfolio-not-invested"
                                      cash
                                      :to="portfolio.cash ? portfolio.cash : 0"
                                      title="Nicht investiert">
            <template #actionsButtons>
              <v-btn :disabled="portfolio.status && portfolio.status.calculationStatus !== 'OK' || isInactivePortfolio"
                     outlined
                     @click="openCashAdminDialog()">
                Bearbeiten
              </v-btn>
            </template>
          </fin-portfolio-value-change>

          <fin-strategy-and-universe-box v-else
                                         class="fin-portfolio-strategy"
                                         :asset-class="assetClass"
                                         :isInactivePortfolio="isInactivePortfolio"
                                         :portfolio.sync="portfolio"
                                         :strategy="portfolioStrategies.get(this.assetClassId)"/>
        </v-col>
      </v-row>

      <v-row class="mt-12" dense>
        <v-col cols="12">
          <h2 class="pb-4">Deine Wertpapiere</h2>
          <p v-if="!hasAssetclassInstrumentEntries">
            In deinem Portfolio befinden sich derzeit keine {{ !assetClass ? 'Wertpapiere' : assetClass.name }}. In der
            <a @click="$emit('openTransactionHistory')">Historie</a> findest
            du alle bisherigen Käufe und Verkäufe.
          </p>
          <fin-portfolio-instruments :instruments.sync="instruments"
                                     :portfolio="portfolio"
                                     :strategy="assetClass ? portfolioStrategies.get(assetClass.id) : undefined"
                                     :isInactivePortfolio="isInactivePortfolio"
                                     class="box-instruments-table fin-portfolio-instruments"
                                     @addToPortfolio="addToPortfolio"
                                     @updatePortfolio="$emit('updatePortfolio')"/>
        </v-col>
      </v-row>

      <v-row class="mt-12" justify="space-between">
        <v-col cols="12" md="7">
          <slot :instrument="instruments" name="timeseries">
            <fin-portfolio-value-chart :assetClass="assetClass" :portfolio="portfolio"
                                       class="fin-portfolio-value-chart" title="Wertentwicklung"
                                       invested-capital/>
          </slot>
        </v-col>
        <v-col cols="12" md="5" class="fin-portfolio-pie-chart">
          <slot :instrument="instruments" name="pieChart">
            <fin-asset-distribution-chart v-if="portfolio.assetAllocationActual || portfolio.assetAllocationTarget"
                                          :actual="portfolio.assetAllocationActual"
                                          :target="portfolio.assetAllocationTarget"/>
          </slot>
        </v-col>
      </v-row>
    </div>

    <div v-else>
      <v-row class="no-content-yet">
        <v-col cols="12" md="4" sm="6" xl="3">
          <h2 v-if="!assetClass">Dein Portfolio ist noch leer</h2>
          <h2 v-else>Dein Portfolio hat noch keine {{ assetClass.name }}</h2>
          <p>
            Leg los und füge
            <span v-if="assetClass">{{ assetClass.name }}</span>
            <span v-else>Wertpapiere</span>
            zu deinem Portfolio hinzu.
          </p>
          <v-btn dark
                 large
                 @click="openAddToPortfolioDialog"
                 class="no-content-yet-add-button"
                 :disabled="isInactivePortfolio">
            <v-icon>icon-plus</v-icon>
            Hinzufügen
          </v-btn>
        </v-col>
        <v-col :offset-lg="`${assetClass ? 0:4}`" :offset-md="`${assetClass ? 0:4}`" :offset-xl="`${assetClass ? 0:4}`"
               cols="12"
               md="4"
               offset="0"
               sm="6"
               xl="3">
          <fin-portfolio-value-change :addSign="false"
                                      cash
                                      class="fin-portfolio-not-invested"
                                      :to="portfolio.cash ? portfolio.cash : 0"
                                      title="Nicht investiert">
            <template #actionsButtons>
              <v-btn outlined
                     @click="showDialogCashAdmin=true"
                     :disabled="isInactivePortfolio">
                Cash bearbeiten
              </v-btn>
            </template>
          </fin-portfolio-value-change>
        </v-col>
        <v-col cols="12" md="4" xl="3">
          <fin-strategy-and-universe-box v-if="assetClass"
                                         :asset-class="assetClass"
                                         :portfolio.sync="portfolio"
                                         class="fin-portfolio-strategy"
                                         :isInactivePortfolio="isInactivePortfolio"
                                         :strategy="portfolioStrategies.get(this.assetClassId)"/>
        </v-col>
      </v-row>

      <div v-if="$vuetify.breakpoint.mdAndUp"
           class="empty-illu">
        <v-row>
          <v-col cols="12" offset="8" class="d-flex justify-end pa-0">
            <v-img :max-width="300"
                   :src="require('@/assets/tools/tool-tools-portfolio.svg')"
                   class="pr-4 py-0"
                   contain
                   style="z-index: 1"/>
          </v-col>
        </v-row>
        <v-row style="margin-top: -205px;">
          <v-col class="pa-0"
                 cols="12" offset="8">
            <fin-chart2 :options="chartOptions"/>
          </v-col>
        </v-row>
      </div>
    </div>

    <fin-dialog v-model="showDialogCashAdmin" :max-width="780">
      <template #title>
        Cash verwalten
      </template>
      <template>
        <v-row>
          <v-col cols="12">
            <p class="dialog__subtitle">Nicht investiert</p>
            <p class="amount">{{ portfolio.cash ? portfolio.cash : 0 | finCurrency }}</p>
          </v-col>

          <v-col cols="12" sm="6" class="py-0">
            <p class="change-field-label mb-2">Cash Betrag</p>
            <fin-currency-field v-model="cash"/>
          </v-col>

          <v-col cols="12" sm="6" class="py-0">
            <p class="change-field-label mb-2">Datum</p>
            <fin-date-picker v-model="date"
                             @input="val => date = val"
                             time-picker/>
          </v-col>

          <v-col v-if="cashAdminError" cols="12">
            <fin-error-display :error-msg="cashAdminError"/>
          </v-col>
        </v-row>
      </template>
      <template #actions>
        <v-col align="right" class="pa-0 d-sm-flex">
          <v-btn :disabled="cash <= 0"
                 outlined
                 :class="$vuetify.breakpoint.xs ? 'mb-1' :'mr-1'"
                 style="min-width: 160px"
                 @click="addToCash()">
            <v-icon class="mr-1">mdi-bank-plus</v-icon>
            Betrag einzahlen
          </v-btn>

          <v-btn :disabled="cash <= 0"
                 outlined
                 style="min-width: 160px"
                 @click="reduceFromCash()">
            <v-icon class="mr-1">mdi-bank-minus</v-icon>
            Betrag entnehmen
          </v-btn>
        </v-col>
      </template>
    </fin-dialog>

  </div>
</template>

<script>
import FinPortfolioValueChange from '@/components/pages/myPortfolio/PortfolioValueChange/index'
import FinAssetDistributionChart from '@/components/pages/myPortfolio/AssetDistributionChart/index'
import FinPortfolioInstruments from '@/components/pages/myPortfolio/PortfolioInstruments/index'
import FinPortfolioValue from "../PortfolioValue/index"
import {mapGetters} from "vuex"
import {finPortfolioAPI} from '@/js/finAPI'
import FinCurrencyField from "../../../common/CurrencyField/index"
import FinChartBuilder from "@/components/common/Chart2/FinChartBuilder"
import FinChart2 from "@/components/common/Chart2/FinChart2"
import FinStrategyAndUniverseBox from "@/components/pages/myPortfolio/StrategyAndUniverseBox"
import FinErrorDisplay from "@/components/common/ErrorDisplay"
import FinPortfolioValueChart from "@/components/pages/myPortfolio/PortfolioValueChart"
import AmplitudePush from "@/mixins/AmplitudePush"
import FinDatePicker from "@/components/common/DatePicker"
import AssetClasses from "@/mocks/AssetClasses"

export default {
  name: 'FinPortfolioOverviewPart',
  mixins: [AmplitudePush],
  components: {
    FinDatePicker,
    FinPortfolioValueChart,
    FinErrorDisplay,
    FinStrategyAndUniverseBox,
    FinChart2,
    FinCurrencyField,
    FinPortfolioValue,
    FinPortfolioInstruments,
    FinAssetDistributionChart,
    FinPortfolioValueChange
  },
  props: {
    portfolioData: {},
    portfolioRatios: undefined,
    assetClass: {},
    distribution: {
      type: Array
    },
    isInactivePortfolio: {
      type: Boolean
    }
  },
  data() {
    return {
      portfolio: this.portfolioData,
      showDialogCashAdmin: false,
      cash: 0,
      shareUniverses: [],
      selectedRange: 5,
      cashAdminError: '',
      date: undefined
    }
  },
  methods: {
    trackToAmplitude(positionOnPage, action) {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_' + positionOnPage + "_" + action,
        'step': action,
        'placement': 'portfolio_details_' + positionOnPage,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    openCashAdminDialog() {
      this.trackToAmplitude('top_cards', 'open_cashdialog')
      this.showDialogCashAdmin = true
      // reset dialog fields
      this.cashAdminError = ''
      this.cash = 0
    },
    openAddToPortfolioDialog() {
      this.trackToAmplitude('top_no_instruments_yet', 'add_instruments')
      this.$emit('addToPortfolioDialog')
    },
    addToPortfolio(item) {
      this.$emit('addToPortfolio', item)
    },
    async addToCash() {
      this.trackToAmplitude('cash_dialog', 'add_cash')
      await finPortfolioAPI.cashPayIn(this.portfolio.id, this.cash, this.date)
        .then(data => {
          this.portfolio.cash = data.cash
          this.cash = 0
          this.$toasted.global.finToast({
            message: 'Der verfügbare Betrag wurde erhöht.',
            type: 'success'
          })
          this.showDialogCashAdmin = false
          this.cashAdminError = ''
          this.$emit('updatePortfolio')
        })
        .catch(error => {
          console.error("Failed to update cash", error)
          let msg = 'Der Betrag konnte nicht hinzugefügt werden.'
          this.cashAdminError = msg
          this.$toasted.global.finToast({
            message: msg,
            type: 'error'
          })
        })
    },
    async reduceFromCash() {
      this.trackToAmplitude('cash_dialog', 'reduce_cash')
      this.loading = true
      await finPortfolioAPI.cashPayOut(this.portfolio.id, this.cash, this.date)
        .then(data => {
          this.loading = false
          this.portfolio.cash = data.cash
          this.cash = 0
          this.$toasted.global.finToast({
            message: 'Der verfügbare Betrag wurde reduziert.',
            type: 'success'
          })
          this.showDialogCashAdmin = false
          this.cashAdminError = ''
          this.$emit('updatePortfolio')
        })
        .catch(error => {
          this.loading = false
          let msg = 'Der Betrag konnte nicht entnommen werden.'
          console.error("Failed to update cash (cashPayOut)", error)
          let jsonErrorInfos = JSON.parse(error.errors[0].message)
          if (jsonErrorInfos.type === "CashExceedsException") {
            msg = 'Der Betrag konnte nicht entnommen werden. Er übersteigt den Kontostand. Es fehlen ' +
              this.$options.filters.currency(jsonErrorInfos.cashDeficit) + "."
          }
          this.cashAdminError = msg
          this.$toasted.global.finToast({
            message: msg,
            type: 'error'
          })
        })
    },
    portfolioInitialValue() {
      /* In overview-Tab: initial Value of the complete portfolio; in an assetclass-Tab: initial value for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      let result = this.getLastTimeSeriesRTValue(assetClassId)
      if (!result || !result.currentValue || !result.growthAmount) {
        return null
      }
      return result.currentValue - result.growthAmount
    },
    portfolioCurrentValue() {
      /* In overview-Tab: current Value of the complete portfolio; in an assetclass-Tab: current value for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      return this.getLastTimeSeriesRTValue(assetClassId)?.currentValue
    },
    portfolioCurrentValueDate() {
      /* In overview-Tab: current Value of the complete portfolio; in an assetclass-Tab: current value for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      return this.getLastTimeSeriesRTValue(assetClassId)?.timestamp
    },
    portfolioOlderThan1Y() {
      /* In overview-Tab: yield1Y of the complete portfolio; in an assetclass-Tab: yield1Y for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      return !!this.getLastTimeSeriesRTValue(assetClassId)?.yield1Y
    },
    portfolioYield() {
      /* In overview-Tab: yield of the complete portfolio; in an assetclass-Tab: yield for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      return this.getLastTimeSeriesRTValue(assetClassId)?.yield
    },
    portfolioYield1D() {
      /* In overview-Tab: yield1D of the complete portfolio; in an assetclass-Tab: yield1D for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      return this.getLastTimeSeriesRTValue(assetClassId)?.yield1D
    },
    portfolioYield1Y() {
      /* In overview-Tab: yield1Y of the complete portfolio; in an assetclass-Tab: yield1Y for this asset class */
      let assetClassId = this.assetClass ? this.assetClassId : "TOTAL"
      return this.getLastTimeSeriesRTValue(assetClassId)?.yield1Y
    },
    getLastTimeSeriesRTValue(assetClassId) {
      const rt = this.portfolioRatios ? this.portfolioRatios.find(item => item.assetClass === assetClassId) : null
      return rt ? rt.ratios : null
    },
  },
  computed: {
    ...mapGetters('auth', ['profileArchetype']),
    hasAssetclassInstrumentEntries() {
      let nbOfInstrumentEntries = 0
      try {
        if (this.assetClass === undefined) {
          nbOfInstrumentEntries = this.portfolio.shareEntries?.items.length + this.portfolio.etfEntries?.items.length
        } else if (this.assetClassId === 'SHARE') {
          nbOfInstrumentEntries = this.portfolio.shareEntries.items?.length
        } else if (this.assetClassId.includes('ETF')) {
          nbOfInstrumentEntries = this.getPortfolioEtfInstrumentsByAssetClass.length
        }
        return nbOfInstrumentEntries > 0
      } catch (e) {
        console.log("hasAssetclassInstrumentEntries: an error occurred", e)
        return nbOfInstrumentEntries > 0
      }
    },
    portfolio1DColor() {
      let result = this.$finColors.accentGreen
      if (this.portfolioYield1D() < 0) {
        result = this.$finColors.accentRed
      }
      return result
    },
    portfolio1YColor() {
      let result = this.$finColors.accentGreen
      if (this.portfolioYield1Y() < 0) {
        result = this.$finColors.accentRed
      }
      return result
    },
    hasAssetClassTransactionHistory() {
      if (this.assetClassId) {
        return !!this.portfolio.transactions.items.some(t => t.instrumentType === this.assetClassId)
      } else {
        return !!this.portfolio.transactions.items.some(t => AssetClasses.items.some(assetClass => assetClass.id === t.instrumentType))
      }
    },
    getPortfolioEtfInstrumentsByAssetClass() {
      return this.portfolio.etfEntries?.items.filter(item => {
        return item.assetClass === this.assetClassId
      })
    },
    chartOptions() {
      return FinChartBuilder.createPlaceholderChart()
    },
    portfolioStrategies() {
      let mapping = new Map()
      mapping.set('SHARE', this.portfolio.shareStrategy)
      mapping.set('SHARE_ETF', this.portfolio.shareETFStrategy)
      mapping.set('CORPORATE_BONDS_ETF', this.portfolio.corporateBondsETFStrategy)
      mapping.set('GOVERNMENT_BONDS_ETF', this.portfolio.governmentBondsETFStrategy)
      mapping.set('COMMODITIES_ETF', this.portfolio.commoditiesETFStrategy)
      return mapping
    },
    instruments: {
      get() {
        let instruments = undefined
        if (this.portfolio && this.assetClass === undefined) {
          instruments = [...this.portfolio.shareEntries.items].concat(this.portfolio.etfEntries.items)
        } else if (this.portfolio && this.assetClassId === 'SHARE' && this.portfolio.shareEntries) {
          instruments = this.portfolio.shareEntries.items
        } else if (this.portfolio && this.assetClassId.includes("ETF") && this.portfolio.etfEntries) {
          instruments = this.getPortfolioEtfInstrumentsByAssetClass
        } else {
          instruments = []
        }
        return instruments
      },
      set(val) {
        if (this.portfolio) {
          this.portfolio.shareEntries.items = val.filter(item => item.assetClass === 'SHARE')
          this.portfolio.etfEntries.items = val.filter(item => item.assetClass !== 'SHARE')
        }

      }
    },
    isStrategy() {
      return this.portfolioStrategies.get(this.assetClassId)
    },
    assetClassId() {
      return this.assetClass?.id
    }
  },
  watch: {
    portfolioData() {
      this.portfolio = this.portfolioData
    },
    showDialogCashAdmin() {
      this.date = new Date()
    }
  }
}
</script>

<style lang="scss" scoped>

@import "../../../../styles/variables.scss";

.portfolio-value-box__hint {
  font-family: $body-font-family;
  font-size: 1em;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-cool-grey;
}

@media only screen and (max-width: 600px) {
  ::v-deep .dropdown {
    top: -30px;
    position: relative;
  }
}

.no-content-yet {
  h2 {
    margin: 0 0 12px;
    font-size: 1.25em !important;
    font-weight: bold;
    line-height: 1.1;
    color: $fin-color-primary-dark;
  }

  p {
    margin: 12px 0 24px;
    font-size: 1.14em;
    line-height: 1.38;
    color: $fin-color-cool-grey;
  }
}

.empty-illu {
  width: 60%;
}

.box-instruments-table, .box-asset-allocation {
  box-shadow: $fin-color-card-shadow;
  border: solid 1px #e0e2e3;
  background-color: white;
}

h2 {
  font-size: 1.7em;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
}

.amount {
  font-family: $body-font-family;
  font-size: 3em;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: normal;
  /* additional styling */
  min-height: 38px;

  @media screen and (max-width: 1264px) {
    font-size: 2.2em;
  }
  @media screen and (max-width: 960px) {
    font-size: 2em;
  }

  .change-field-label {
    font-size: 16px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
  }
}

::v-deep .currency-inputfield .v-input__slot {
  height: 56px;
}
</style>
